import { useState } from 'react';
import type { Asignacion, Rrpp, Tarifa } from '../../../api/ops/listas';
import type { EventoPanel } from '../../../api/ops/portalRrpp';
import { Boton } from '../../../componentes/Boton';
import { Chip } from '../../../componentes/Chip';
import { Contexto } from '../../../componentes/Contexto';
import { Desplegable } from '../../../componentes/Desplegable';
import { Filtros } from '../../../componentes/Filtros';
import { Icono } from '../../../componentes/Icono';
import { Tabla, type FilaTabla } from '../../../componentes/Tabla';
import { dinero } from '../../../logica/dinero';
import { useCabecera, type AccionCabecera } from '../../../marco/cabecera';
import { usePermisos } from '../../../permisos/permisos';
import type { PropsPantalla } from '../../tipos';
import { AvisoVista, Bloque, Dato, Vacia } from '../comun';
import { iniciales, useCarga } from '../uso';
import { Cargando, EstadoDelError, SinPermiso } from '../estados';
import { AYUDA, BASE, comoSeCalcula, cuandoEs, cupoTexto, eventoAbierto, eventoInicial, motivosDeEnlace, NIVEL, nombreRrpp, opsDe, ORDEN_NIVELES, paraQuien, PERIODOS_SOCIO, periodoSocio, PERMISOS, type PeriodoSocio } from '../textos';
import { PanelAsignacion } from './Asignacion';
import { FichaRrpp } from './FichaRrpp';
import { InvitacionesRrpp, Invitar } from './Invitar';
import { PanelTarifa } from './Tarifa';

/**
 * Apartado RRPP (10.10): Tus RRPP (con su código, eventos y lo pendiente; invitar con `invitePromoter`, G789), En el evento
 * (cupos, listas y enlace de cada RRPP; asignar) y Comisiones (tarifas en escalera lista → evento → RRPP → casa). Quien
 * solo tiene `rrpp.informes.ver` (el Socio) ve las cifras agregadas de `getCommissionSummary`, sin invitados ni pagos.
 */
export type PanelRrpp =
    | { tipo: 'asignar'; miembro: string | null; pestana?: number }
    | { tipo: 'rrpp'; id: string }
    | { tipo: 'invitar' }
    | { tipo: 'tarifa'; tarifa: Tarifa | null };

export interface ContextoRrpp {
    props: PropsPantalla;
    eventos: readonly EventoPanel[];
    evento: EventoPanel | null;
    rrpp: readonly Rrpp[];
    asignaciones: readonly Asignacion[];
    abrir: (p: PanelRrpp | null) => void;
    recargar: () => void;
}

export function ApartadoRrpp(props: PropsPantalla) {
    const permisos = usePermisos();
    if (!permisos.puede(PERMISOS.rrppVer)) {
        return permisos.puede(PERMISOS.informes) ? <CifrasSocio props={props} /> : <SinPermiso props={props} seccion="RRPP" />;
    }
    return <RrppDelNegocio props={props} />;
}

function RrppDelNegocio({ props }: { props: PropsPantalla }) {
    const { negocio, movil, soloLectura, busqueda, navegar } = props;
    const ops = opsDe(props.ops);
    const permisos = usePermisos();
    const vistas = [
        { clave: 'todos', etiqueta: 'Tus RRPP' },
        { clave: 'evento', etiqueta: 'En el evento' },
        ...(permisos.puede(PERMISOS.comisionesVer) ? [{ clave: 'tarifas', etiqueta: 'Comisiones' }] : []),
    ];
    const [vista, setVista] = useState(busqueda.get('vista') ?? 'todos');
    const [eventoId, setEventoId] = useState<string | null>(busqueda.get('evento'));
    const [panel, setPanel] = useState<PanelRrpp | null>(null);
    const [vuelta, setVuelta] = useState(0);
    const rrpp = useCarga(() => ops.listPromoters(negocio, { limit: 100 }), [negocio, vuelta]);
    const eventos = useCarga(() => ops.listEvents(negocio, { from: new Date(Date.now() - 30 * 86_400_000).toISOString(), limit: 50 }), [negocio]);
    const evento = eventos.datos === null ? null : eventoInicial(eventos.datos.data, eventoId);
    const idEvento = evento?.id ?? null;
    const asignaciones = useCarga(idEvento === null ? null : () => ops.listPromoterAssignments(negocio, idEvento), [negocio, idEvento, vuelta]);
    const ctx: ContextoRrpp = {
        props,
        eventos: eventos.datos?.data ?? [],
        evento,
        rrpp: rrpp.datos?.data ?? [],
        asignaciones: asignaciones.datos?.data ?? [],
        abrir: setPanel,
        recargar: () => { setVuelta((v) => v + 1); },
    };

    const acciones: AccionCabecera[] = [];
    if (vista === 'todos' && permisos.puede(PERMISOS.rrppInvitar) && !soloLectura) {
        acciones.push({ clave: 'invitar', texto: 'Nuevo RRPP', icono: 'user-plus', onClick: () => { setPanel({ tipo: 'invitar' }); } });
    }
    if (vista === 'evento' && permisos.puede(PERMISOS.asignar) && !soloLectura && eventoAbierto(evento)) {
        acciones.push({ clave: 'asignar', texto: 'Asignar RRPP', icono: 'user-plus', onClick: () => { setPanel({ tipo: 'asignar', miembro: null }); } });
    }
    if (vista === 'tarifas' && permisos.puede(PERMISOS.tarifas) && !soloLectura) {
        acciones.push({ clave: 'tarifa', texto: 'Nueva tarifa', icono: 'plus', onClick: () => { setPanel({ tipo: 'tarifa', tarifa: null }); } });
    }
    useCabecera({ ayuda: AYUDA[vista] ?? [], acciones });

    if (rrpp.error !== null && rrpp.datos === null) {
        return <EstadoDelError props={props} error={rrpp.error} seccion="RRPP" nombre="los RRPP" onReintentar={rrpp.recargar} />;
    }
    if (rrpp.datos === null) {
        return <Cargando nombre="los RRPP" />;
    }
    const indice = Math.max(0, vistas.findIndex((v) => v.clave === vista));
    const enlacesDelEvento = ctx.asignaciones.map((a) => a.event_link).filter((e) => e != null);
    const motivos = enlacesDelEvento.flatMap((e) => motivosDeEnlace(e));
    return (
        <div className="rv-ls-apartado">
            <div className="rv-ls-vistas">
                <Filtros etiqueta="Vista" items={vistas.map((v) => ({ etiqueta: v.etiqueta }))} valor={indice} onCambio={(i) => { setVista(vistas[i]?.clave ?? 'todos'); }} />
            </div>
            {vista === 'todos' && <VistaTodos ctx={ctx} />}
            {vista === 'evento' && (
                <>
                    {evento !== null && (
                        <div className="rv-ls-contexto">
                            <Contexto etiqueta="Evento" icono="ticket" movil={movil} valor={evento.id} opciones={ctx.eventos.map((e) => ({ valor: e.id, texto: `${e.name ?? ''} · ${cuandoEs(e.starts_at)}` }))} onChange={setEventoId} />
                        </div>
                    )}
                    {motivos.includes('Instagram sin conectar') && (
                        <AvisoVista
                            icono="instagram"
                            titulo="Instagram no está conectado."
                            texto={`Los enlaces de Instagram de los RRPP no salen hasta que conectes la cuenta de Instagram del negocio.${motivos.includes('web sin publicar') ? '' : ' Su formulario web sí funciona.'}`}
                            boton={permisos.puede(PERMISOS.canalesGestionar) ? { texto: 'Conectar Instagram', icono: 'plug', onClick: () => { navegar('/canales?canal=instagram'); } } : null}
                        />
                    )}
                    {motivos.includes('web sin publicar') && (
                        <AvisoVista icono="globe" titulo="Tu web no está publicada." texto="Los formularios web de los RRPP no funcionan hasta que la publiques." boton={permisos.puede(PERMISOS.webPublicar) ? { texto: 'Ir a la web', icono: 'globe', onClick: () => { navegar('/web'); } } : null} />
                    )}
                    <VistaEvento ctx={ctx} cargando={asignaciones.datos === null && idEvento !== null} />
                </>
            )}
            {vista === 'tarifas' && <VistaTarifas ctx={ctx} />}

            {panel?.tipo === 'asignar' && <PanelAsignacion ctx={ctx} miembro={panel.miembro} pestanaInicial={panel.pestana ?? 0} />}
            {panel?.tipo === 'rrpp' && <FichaRrpp ctx={ctx} rrppId={panel.id} />}
            {panel?.tipo === 'invitar' && <Invitar ctx={ctx} />}
            {panel?.tipo === 'tarifa' && <PanelTarifa ctx={ctx} tarifa={panel.tarifa} />}
        </div>
    );
}

function VistaTodos({ ctx }: { ctx: ContextoRrpp }) {
    const { props, rrpp } = ctx;
    const { movil, soloLectura } = props;
    const permisos = usePermisos();
    if (rrpp.length === 0) {
        return (
            <Vacia icono="users" titulo="Todavía no tienes RRPP." texto="Invítalos con su email: les llega un enlace para unirse con el rol RRPP y entrar en su portal.">
                {permisos.puede(PERMISOS.rrppInvitar) && !soloLectura && <Boton icono="user-plus" onClick={() => { ctx.abrir({ tipo: 'invitar' }); }}>Nuevo RRPP</Boton>}
            </Vacia>
        );
    }
    const filas: FilaTabla[] = rrpp.map((p) => {
        const activo = p.status === 'active';
        const nombre = nombreRrpp(p);
        return {
            clave: p.id,
            etiqueta: nombre,
            onAbrir: () => { ctx.abrir({ tipo: 'rrpp', id: p.id }); },
            celdas: {
                rrpp: (
                    <span className="rv-ls-celda-persona">
                        <span className={`rv-ca-inicial${activo ? ' rv-ca-inicial--roja' : ''}`} aria-hidden="true">{iniciales(nombre)}</span>
                        <span className="rv-ca-fila__texto">
                            <strong>{nombre}{!activo && <Chip estado="neutral">Desactivado</Chip>}</strong>
                            <span>{activo ? 'Activo' : 'Desactivado: se le liquida lo pendiente'}</span>
                        </span>
                    </span>
                ),
                codigo: <span className="rv-ca-fila__texto"><strong>{p.sales_code}</strong><span>Código de venta</span></span>,
                pendiente: <span className="rv-ls-importe"><strong>{dinero(p.pending_total)}</strong><small>{(p.pending_total?.amount ?? 0) > 0 ? 'por liquidar' : 'nada pendiente'}</small></span>,
            },
            tarjeta: (
                <span className="rv-ca-fila__texto">
                    <strong>{nombre}{!activo && <Chip estado="neutral">Desactivado</Chip>}</strong>
                    <span>Código {p.sales_code} · {dinero(p.pending_total)} pendiente</span>
                </span>
            ),
        };
    });
    return (
        <>
            {!permisos.puede(PERMISOS.rrppInvitar) && <AvisoVista tono="info" icono="user-plus" titulo="Para dar de alta un RRPP," texto="pídeselo al propietario o a un administrador: lo invitan desde aquí o desde Equipo y roles." />}
            {permisos.puede(PERMISOS.rrppInvitar) && <InvitacionesRrpp ctx={ctx} />}
            <Tabla
                etiqueta="RRPP del negocio"
                movil={movil}
                columnas={[
                    { clave: 'rrpp', titulo: 'RRPP', ancho: 'minmax(220px, 1.6fr)' },
                    { clave: 'codigo', titulo: 'Código', ancho: 'minmax(140px, 0.8fr)' },
                    { clave: 'pendiente', titulo: 'Pendiente', ancho: 'minmax(130px, 0.8fr)', alinear: 'derecha' },
                ]}
                filas={filas}
            />
        </>
    );
}

function VistaEvento({ ctx, cargando }: { ctx: ContextoRrpp; cargando: boolean }) {
    const { props, evento, asignaciones, rrpp } = ctx;
    const { movil, soloLectura } = props;
    const permisos = usePermisos();
    if (evento === null) {
        return <Vacia icono="ticket" titulo="Todavía no hay eventos." texto="Crea un evento para asignarle tus RRPP." />;
    }
    if (cargando) {
        return <Cargando nombre="los RRPP del evento" />;
    }
    const vivas = asignaciones.filter((a) => a.status === 'active');
    const terminado = evento.status === 'ended';
    if (vivas.length === 0) {
        return (
            <Vacia icono="users" titulo="Ningún RRPP en este evento." texto={evento.status === 'draft' ? 'Asígnalos ahora: sus enlaces empezarán a funcionar al publicar el evento.' : 'Asigna a tus RRPP con sus cupos y cada uno tendrá su enlace para este evento.'}>
                {permisos.puede(PERMISOS.asignar) && !soloLectura && eventoAbierto(evento) && rrpp.some((p) => p.status === 'active') && (
                    <Boton icono="user-plus" onClick={() => { ctx.abrir({ tipo: 'asignar', miembro: null }); }}>Asignar RRPP</Boton>
                )}
            </Vacia>
        );
    }
    const porMiembro = new Map(rrpp.map((p) => [p.member_id, p]));
    const filas: FilaTabla[] = vivas.map((a) => {
        const p = porMiembro.get(a.member_id);
        const nombre = nombreRrpp(p);
        const lk = a.event_link;
        const vivo = lk != null && lk.status === 'active';
        const lleno = a.event_quota != null && a.event_used >= a.event_quota;
        return {
            clave: a.id,
            etiqueta: nombre,
            onAbrir: () => { ctx.abrir({ tipo: 'asignar', miembro: a.member_id }); },
            celdas: {
                rrpp: (
                    <span className="rv-ls-celda-persona">
                        <span className="rv-ca-inicial rv-ca-inicial--roja" aria-hidden="true">{iniciales(nombre)}</span>
                        <span className="rv-ca-fila__texto"><strong>{nombre}</strong><span>Código {p?.sales_code ?? '—'}</span></span>
                    </span>
                ),
                cupo: (
                    <span className="rv-ls-barrita">
                        <strong>{cupoTexto(a.event_quota, a.event_used)}</strong>
                        <span className="rv-ls-barrita__fondo"><span style={{ width: `${String(a.event_quota ? Math.min(100, Math.round((a.event_used / a.event_quota) * 100)) : 0)}%`, background: lleno ? 'var(--rv-red)' : undefined }} /></span>
                    </span>
                ),
                listas: <span className="rv-ls-texto-medio">{a.lists.filter((x) => x.quota !== 0).length} {a.lists.filter((x) => x.quota !== 0).length === 1 ? 'lista' : 'listas'} con cupo</span>,
                enlace: vivo ? (
                    <span className="rv-ca-fila__texto"><strong>{terminado ? 'Cerrado' : `${String(lk.signups ?? 0)} apuntados`}</strong><span>{terminado ? `${String(lk.signups ?? 0)} se apuntaron con él` : 'con su enlace de Instagram'}</span></span>
                ) : (
                    <span className="rv-ca-fila__texto"><strong><Chip estado="problem">Desactivado</Chip></strong><span>Sin enlace activo</span></span>
                ),
            },
            tarjeta: (
                <span className="rv-ca-fila__texto">
                    <strong>{nombre}{!vivo && <Chip estado="problem">Desactivado</Chip>}</strong>
                    <span>{cupoTexto(a.event_quota, a.event_used)}{vivo ? ` · ${String(lk.signups ?? 0)} por su enlace` : ''}</span>
                </span>
            ),
            acciones: (
                <button type="button" className="rv-ls-fantasma" aria-label={`Ver el enlace de ${nombre}`} onClick={(e) => { e.stopPropagation(); ctx.abrir({ tipo: 'asignar', miembro: a.member_id, pestana: 1 }); }}>
                    <Icono nombre="link" />
                </button>
            ),
        };
    });
    return (
        <Tabla
            etiqueta="RRPP del evento"
            movil={movil}
            columnas={[
                { clave: 'rrpp', titulo: 'RRPP', ancho: 'minmax(190px, 1.2fr)' },
                { clave: 'cupo', titulo: 'Cupo en el evento', ancho: 'minmax(140px, 0.9fr)' },
                { clave: 'listas', titulo: 'Listas', ancho: 'minmax(140px, 1fr)' },
                { clave: 'enlace', titulo: 'Enlace de Instagram', ancho: 'minmax(160px, 1fr)' },
            ]}
            filas={filas}
        />
    );
}


function VistaTarifas({ ctx }: { ctx: ContextoRrpp }) {
    const { props } = ctx;
    const { negocio, movil, soloLectura } = props;
    const ops = opsDe(props.ops);
    const permisos = usePermisos();
    const tarifas = useCarga(() => ops.listCommissionRates(negocio, { status: 'active', limit: 100 }), [negocio, ctx.rrpp]);
    if (tarifas.error !== null && tarifas.datos === null) {
        return <EstadoDelError props={props} error={tarifas.error} seccion="RRPP" nombre="las comisiones" onReintentar={tarifas.recargar} />;
    }
    if (tarifas.datos === null) {
        return <Cargando nombre="las comisiones" />;
    }
    const orden = [...tarifas.datos.data].sort((x, y) => ORDEN_NIVELES.indexOf(x.level) - ORDEN_NIVELES.indexOf(y.level));
    const puede = permisos.puede(PERMISOS.tarifas) && !soloLectura;
    return (
        <>
            {!permisos.puede(PERMISOS.tarifas) && <AvisoVista tono="info" icono="eye" titulo="Con tu rol ves las comisiones." texto="Las cambia el propietario o un administrador." />}
            {orden.length === 0 ? (
                <Vacia icono="percent" titulo="Todavía no hay comisiones." texto="Empieza por la de la casa: lo que gana cualquier RRPP por cada invitado que entra o cada entrada que vende.">
                    {puede && <Boton icono="plus" onClick={() => { ctx.abrir({ tipo: 'tarifa', tarifa: null }); }}>Nueva tarifa</Boton>}
                </Vacia>
            ) : (
                <Tabla
                    etiqueta="Tarifas de comisión"
                    movil={movil}
                    columnas={[
                        { clave: 'para', titulo: 'Para', ancho: 'minmax(220px, 1.5fr)' },
                        { clave: 'base', titulo: 'Por cada', ancho: 'minmax(170px, 1fr)' },
                        { clave: 'comision', titulo: 'Comisión', ancho: 'minmax(220px, 1.4fr)' },
                    ]}
                    filas={orden.map((t) => {
                        const [para, paraSub] = paraQuien(t, ctx);
                        const [como, comoSub] = comoSeCalcula(t);
                        return {
                            clave: t.id,
                            etiqueta: para,
                            onAbrir: () => { ctx.abrir({ tipo: 'tarifa', tarifa: t }); },
                            celdas: {
                                para: (
                                    <span className="rv-ls-celda-persona">
                                        <span className="rv-ca-baldosa" style={t.level === 'house' ? { background: 'var(--rv-ink)', color: '#fff' } : undefined}><Icono nombre={NIVEL[t.level][1]} /></span>
                                        <span className="rv-ca-fila__texto"><strong>{para}</strong><span>{paraSub}</span></span>
                                    </span>
                                ),
                                base: <span className="rv-ls-texto-medio">{BASE[t.basis][0]}</span>,
                                comision: <span className="rv-ca-fila__texto"><strong>{como}</strong>{comoSub !== '' && <span>{comoSub}</span>}</span>,
                            },
                            tarjeta: (
                                <span className="rv-ca-fila__texto"><strong>{para} · {como}</strong><span>{BASE[t.basis][0]} · {NIVEL[t.level][0]}</span></span>
                            ),
                        };
                    })}
                />
            )}
            <p className="rv-ca-nota rv-ca-nota--gris"><Icono nombre="layers" />Manda la más específica: la de la lista, luego la del evento, la del RRPP y, si no hay ninguna, la de la casa.</p>
        </>
    );
}


function CifrasSocio({ props }: { props: PropsPantalla }) {
    const { negocio, movil } = props;
    const ops = opsDe(props.ops);
    const [p, setP] = useState<PeriodoSocio>('mes');
    const per = periodoSocio(p);
    const resumen = useCarga(() => ops.getCommissionSummary(negocio, { from: per.from, to: per.to }), [negocio, per.from, per.to]);
    const eventosSocio = useCarga(() => ops.listEvents(negocio, { from: `${per.from}T00:00:00Z`, limit: 100 }), [negocio, per.from]);
    useCabecera({ ayuda: AYUDA.socio ?? [], acciones: [] });
    if (resumen.error !== null && resumen.datos === null) {
        return <EstadoDelError props={props} error={resumen.error} seccion="RRPP" nombre="las cifras de los RRPP" onReintentar={resumen.recargar} />;
    }
    // Una fila por RRPP y evento con su nombre (G836) y las sumas del periodo de la API (`totals`, G834): el panel no suma.
    const eventos = new Map((eventosSocio.datos?.data ?? []).map((e) => [e.id, e]));
    const t = resumen.datos?.totals;
    return (
        <div className="rv-ls-apartado">
            <AvisoVista tono="info" icono="eye" titulo="Con tu rol ves solo cifras." texto="Sin datos de los invitados ni de los pagos a cada RRPP." />
            <div className="rv-ls-herramientas">
                <Desplegable etiqueta="Periodo" valor={p} onChange={(v) => { setP(v as PeriodoSocio); }} opciones={PERIODOS_SOCIO.map((x) => ({ valor: x, texto: periodoSocio(x).texto }))} />
            </div>
            {resumen.datos === null ? (
                <Cargando nombre="las cifras de los RRPP" />
            ) : resumen.datos.rows.length === 0 ? (
                <Vacia icono="chart-column" titulo="Todavía no hay cifras." texto="Cuando los RRPP apunten invitados o vendan entradas, verás aquí sus cifras." />
            ) : (
                <>
                {t !== undefined && (
                    <Bloque>
                        <Dato icono="users" k="Invitados" v={String(t.guests_registered ?? 0)} sub={`${String(t.guests_arrived ?? 0)} llegaron`} />
                        <Dato icono="ticket" k="Ventas" v={String(t.tickets_attributed ?? 0)} sub="entradas" />
                        <Dato icono="wallet" k="Por liquidar" v={dinero(t.pending)} sub={(t.provisional?.amount ?? 0) !== 0 ? `${dinero(t.provisional)} provisional` : undefined} />
                        <Dato icono="circle-check" k="Liquidado" v={dinero(t.settled)} />
                    </Bloque>
                )}
                <Tabla
                    etiqueta="Cifras de los RRPP"
                    movil={movil}
                    columnas={[
                        { clave: 'rrpp', titulo: 'RRPP', ancho: 'minmax(160px, 1.1fr)' },
                        { clave: 'invitados', titulo: 'Invitados', ancho: 'minmax(100px, 0.7fr)', alinear: 'derecha' },
                        { clave: 'ventas', titulo: 'Ventas', ancho: 'minmax(90px, 0.6fr)', alinear: 'derecha' },
                        { clave: 'pendiente', titulo: 'Por liquidar', ancho: 'minmax(120px, 0.8fr)', alinear: 'derecha' },
                        { clave: 'liquidado', titulo: 'Liquidado', ancho: 'minmax(120px, 0.8fr)', alinear: 'derecha' },
                    ]}
                    filas={resumen.datos.rows.map((r) => {
                        const e = eventos.get(r.event_id ?? '');
                        const nombre = r.display_name ?? `RRPP ${(r.promoter_id ?? '').slice(-6)}`;
                        return {
                            clave: `${r.promoter_id ?? ''}-${r.event_id ?? ''}`,
                            etiqueta: nombre,
                            celdas: {
                                rrpp: <span className="rv-ca-fila__texto"><strong>{nombre}</strong><span>{e === undefined ? 'Evento' : `${e.name ?? ''} · ${cuandoEs(e.starts_at)}`}</span></span>,
                                invitados: <span className="rv-ls-importe"><strong>{r.guests_registered ?? 0}</strong><small>{r.guests_arrived ?? 0} llegaron</small></span>,
                                ventas: <span className="rv-ls-importe"><strong>{r.tickets_attributed ?? 0}</strong><small>entradas</small></span>,
                                pendiente: <span className="rv-ls-importe"><strong>{dinero(r.pending)}</strong><small>{(r.provisional?.amount ?? 0) !== 0 ? `${dinero(r.provisional)} provisional` : 'pendiente'}</small></span>,
                                liquidado: <span className="rv-ls-importe"><strong>{dinero(r.settled)}</strong><small>liquidado</small></span>,
                            },
                        };
                    })}
                />
                </>
            )}
        </div>
    );
}
